import { useEffect, useState } from "react";
import getButtonStyle from "../../utils/styles";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { getAuth, login } from "../../slices/auth";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";
import Banner from "../layout/Banner";
import { useNavigate } from "react-router-dom";
import TextInput from "../forms/TextInput";
import Button from "../layout/Button";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";

export default function Login() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, user, loading } = useAppSelector(
    (state) => state.auth,
  );

  useEffect(() => {
    if (user || isAuthenticated) {
      navigate("/");
    }
  }, [navigate, user, isAuthenticated]);

  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });

  async function submitLoginForm(e: any) {
    e.preventDefault();
    const { username, password } = formData;
    const res: any = await dispatch(login({ username, password }));
    const language = !user ? "EN" : user.language;
    if (res.payload.status === 200) {
      dispatch(addAlert(generateAlertText("login", language), "success", 5000));
      dispatch(getAuth());
    } else {
      dispatch(
        addAlert(generateAlertText("login_error", language), "danger", 5000),
      );
    }
  }

  return (
    <>
      <div className="relative flex h-screen min-h-full justify-center bg-gradient-to-r from-info to-primary background-animate md:px-12 lg:px-0">
        <div className="relative z-10 flex flex-1 flex-col bg-midnight px-4 py-10 shadow-2xl sm:justify-center md:flex-none md:px-28">
          <div className="mx-auto w-full max-w-md sm:px-4 md:w-96 md:max-w-sm md:px-0">
            <div className="flex flex-col">
              {/* Header */}
              <a href="/" aria-label="Home">
                <span className="sr-only">audico</span>
                <img
                  className="h-20 w-auto"
                  src="/img/logo/icon_gradient.svg"
                  alt="audico home logo"
                />
              </a>
              <div className="mt-20">
                <h2 className="gotham-bold text-lg font-semibold text-white">
                  Log in to your account
                </h2>
                <p className="mt-2 text-sm text-light">
                  Don't have an account?{" "}
                  <a
                    href="https://audico.co.uk/contact"
                    className="gotham-bold font-medium text-info hover:underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Get in touch
                  </a>{" "}
                  to register your account.
                </p>
              </div>
            </div>

            {/* Login form */}
            {loading ? (
              <SpaceFiller>
                <Spinner />
              </SpaceFiller>
            ) : (
              <form
                onSubmit={(e) => submitLoginForm(e)}
                className="mt-10 grid grid-cols-1 gap-y-8"
              >
                {/* Email */}
                <TextInput
                  id="username"
                  type="text"
                  label="Username"
                  labelColour="text-light"
                  className="block w-full appearance-none rounded-md bg-light/20 px-3 py-2 text-white placeholder-light focus:border-primary focus:bg-light/20 focus:outline-none focus:ring-primary sm:text-sm"
                  autoComplete="username"
                  required={true}
                  showLabel={true}
                  state={formData}
                  stateName="username"
                  onChange={setFormData}
                />

                {/* Password */}
                <TextInput
                  id="password"
                  type="password"
                  label="Password"
                  labelColour="text-light"
                  className="block w-full appearance-none rounded-md bg-light/20 px-3 py-2 text-white placeholder-light focus:border-primary focus:bg-light/20 focus:outline-none focus:ring-primary sm:text-sm"
                  autoComplete="current-password"
                  required={true}
                  showLabel={true}
                  state={formData}
                  stateName="password"
                  onChange={setFormData}
                />

                {/* Submit button */}
                <div>
                  <Button
                    type="submit"
                    className={`w-full ${getButtonStyle(
                      "solid",
                      "primary",
                    )} rounded-full`}
                  >
                    <span>
                      Sign in <span aria-hidden="true">&rarr;</span>
                    </span>
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
        <div className="hidden sm:contents lg:relative lg:block lg:flex-1" />
      </div>
      <Banner />
    </>
  );
}
